import { useState } from 'react';
import { App, Button, Collapse, Input } from 'antd';
import { ApiOutlined } from '@ant-design/icons';
import {
  fetchGraphQLSchema,
  formatTypeRef,
  type GraphQLSchemaInfo,
} from '@presentation/services/graphqlIntrospection';

interface GraphQLExplorerPanelProps {
  url: string;
  headers: Record<string, string>;
}

/**
 * A read-only schema browser (root Query/Mutation/Subscription fields with
 * their argument and return-type signatures) to reference while writing a
 * query — not a full interactive query-builder like GraphiQL, which is a
 * substantially larger feature than fits this scope.
 */
export const GraphQLExplorerPanel = ({ url, headers }: GraphQLExplorerPanelProps) => {
  const { message } = App.useApp();
  const [schema, setSchema] = useState<GraphQLSchemaInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const fetchSchema = async () => {
    if (!url.trim()) {
      void message.error('Enter a URL first');
      return;
    }

    setLoading(true);
    try {
      const result = await fetchGraphQLSchema(url, headers);
      setSchema(result);
      void message.success(`Schema loaded — ${result.types.length} types`);
    } catch (error) {
      void message.error(error instanceof Error ? error.message : 'Failed to fetch schema');
    } finally {
      setLoading(false);
    }
  };

  const rootTypeNames = schema
    ? [schema.queryType, schema.mutationType, schema.subscriptionType].filter(
        (name): name is string => Boolean(name),
      )
    : [];
  const rootTypes = schema ? schema.types.filter((type) => rootTypeNames.includes(type.name)) : [];

  const term = search.trim().toLowerCase();
  const visibleRootTypes = term
    ? rootTypes
        .map((type) => ({
          ...type,
          fields: (type.fields ?? []).filter((field) => field.name.toLowerCase().includes(term)),
        }))
        .filter((type) => type.fields.length > 0)
    : rootTypes;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <Button
          size="small"
          icon={<ApiOutlined />}
          loading={loading}
          onClick={() => void fetchSchema()}
        >
          Fetch schema
        </Button>
        {schema && (
          <span className="text-xs text-gray-400">{schema.types.length} types loaded</span>
        )}
      </div>

      {schema && (
        <>
          <Input
            size="small"
            placeholder="Search fields"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            allowClear
          />
          <div className="max-h-64 overflow-y-auto rounded border border-black/10 dark:border-white/10">
            <Collapse
              size="small"
              ghost
              defaultActiveKey={rootTypes.map((type) => type.name)}
              items={visibleRootTypes.map((type) => ({
                key: type.name,
                label: `${type.name} (${(type.fields ?? []).length})`,
                children: (
                  <div className="flex flex-col gap-2 pl-2">
                    {(type.fields ?? []).map((field) => (
                      <div key={field.name} className="text-xs">
                        <div className="font-mono">
                          <span className="font-medium">{field.name}</span>
                          {field.args.length > 0 && (
                            <span className="text-gray-400">
                              (
                              {field.args
                                .map((arg) => `${arg.name}: ${formatTypeRef(arg.type)}`)
                                .join(', ')}
                              )
                            </span>
                          )}
                          <span className="text-blue-400">: {formatTypeRef(field.type)}</span>
                        </div>
                        {field.description && (
                          <div className="text-gray-400">{field.description}</div>
                        )}
                      </div>
                    ))}
                    {(type.fields ?? []).length === 0 && (
                      <span className="text-xs text-gray-400">No matching fields</span>
                    )}
                  </div>
                ),
              }))}
            />
          </div>
        </>
      )}
    </div>
  );
};
